import React from 'react';
import { AIActivator, getTranslatedText, useIntl } from '@nile/components';
import { Button } from '@superops/ui-design-system';
import { FormattedMessage } from 'react-intl';

interface ButtonConfig {
  label?: string;
  type: string;
  featureName?: string; // For activator type
  component?: React.ComponentType<any>; // For custom type
  props?: Record<string, any>; // Props to pass to custom component
  showResponseBubble?: boolean;
  responseBubbleText?: string;
}

interface RenderActionButtonsProps {
  stepIndex: number;
  config: any;
  hiddenButtons: Map<number, Set<number>>;
  onButtonAction: (buttonConfig: any, index: number, stepIndex: number) => void;
  containerClassName?: string;
}

const AIActivatorWrapper: React.FC<{
  featureName: string;
  label?: string;
  onClick?: () => void;
}> = React.memo(({ featureName, label, onClick }) => {
  const intl = useIntl();

  return (
    <AIActivator
      featureName={featureName}
      // icon="AIWand"
      disabledTooltip={'Please enable the AI agent from agent marketplace.'}
      // ai.onboarding.startagent.disabled.tooltip
      // disabledTooltip={getTranslatedText(
      //   intl,
      //   'msp.ai.ticket.category.summary.disabled.tooltip'
      // )}
      buttonVariant="aiPrimaryFill"
      buttonSize="slim"
      buttonText={label ? label : getTranslatedText(intl, 'common.summarize')}
      fullWidth
      hideIcon
      customClassName="br-5 center flex full-width p5 pointer text-align-center jctr"
      onClick={(params) => {
        onClick && onClick();
      }}
    />
  );
});

const RenderActionButtons: React.FC<RenderActionButtonsProps> = React.memo(
  ({
    stepIndex,
    config,
    hiddenButtons,
    onButtonAction,
    containerClassName = 'flex g10 mt10 position-absolute full-width pt15'
  }) => {
    const intl = useIntl();

    if (!config || !config.customSteps || config.customSteps.length === 0)
      return null;

    const currentStep = config.customSteps[stepIndex] as any;
    if (!currentStep || !currentStep.buttonConfig) return null;

    const buttons = React.useMemo(() => {
      return currentStep.buttonConfig.map(
        (buttonConfig: ButtonConfig, index: number) => {
          // Don't render button if it's hidden for this specific step
          // const stepHiddenButtons = hiddenButtons.get(stepIndex);
          // if (stepHiddenButtons && stepHiddenButtons.has(index)) {
          //   return null;
          // }
          if (!buttonConfig) {
            return null;
          }
          if (buttonConfig.type === 'activator') {
            return (
              <AIActivatorWrapper
                key={index}
                featureName={buttonConfig.featureName || ''}
                label={
                  buttonConfig.label
                    ? getTranslatedText(intl, buttonConfig.label)
                    : getTranslatedText(intl, 'onboarding.agent.startagent')
                }
                onClick={() => onButtonAction(buttonConfig, index, stepIndex)}
              />
            );
          }
          if (buttonConfig.type === 'custom') {
            return (
              <Button
                key={index}
                fullWidth
                {...(buttonConfig?.buttonVariant
                  ? { [buttonConfig.buttonVariant]: true }
                  : {})}
                onClick={() => onButtonAction(buttonConfig, index, stepIndex)}
              >
                <FormattedMessage id={buttonConfig.label} />
              </Button>
            );
          }

          return (
            <Button
              key={index}
              primary
              aiPrimary
              onClick={() => onButtonAction(buttonConfig, index, stepIndex)}
            >
              <FormattedMessage id={buttonConfig.label} />
            </Button>
          );
        }
      );
    }, [currentStep.buttonConfig, hiddenButtons, stepIndex, onButtonAction]);

    return (
      <div
        className={containerClassName}
        style={{
          bottom: '-1px',
          background: 'var(--sops-ai-dark-bg-gradient)',
          zIndex: 1000,
          boxSizing: 'content-box'
        }}
      >
        {buttons}
      </div>
    );
  }
);

export const renderActionButtons = (props: RenderActionButtonsProps) => {
  return <RenderActionButtons {...props} />;
};

export default renderActionButtons;
